import Link from 'next/link'

import { cn } from '@/lib/utils'

import type { EdunodesCommunitySummary } from '@/constant/edunodes'
import {
  EDUNODES_HOME_URL,
  getEdunodesCommunitySiteHost,
  getEdunodesCommunitySiteUrl,
} from '@/constant/edunodes'

type EdunodesExistingAccountNoticeProps = {
  communities: EdunodesCommunitySummary[]
  className?: string
  /** Sign-up shows action links; forgot-password references the form below. */
  variant?: 'sign-up' | 'forgot-password'
  email?: string
}

function CommunitySiteLink({ community }: { community: EdunodesCommunitySummary }) {
  const host = getEdunodesCommunitySiteHost(community)
  return (
    <a
      href={getEdunodesCommunitySiteUrl(community)}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-blue-700 underline-offset-2 hover:underline"
    >
      {host}
    </a>
  )
}

function formatCommunitySitesPhrase(communities: EdunodesCommunitySummary[]) {
  if (communities.length === 0) {
    return null
  }
  return communities.map((community, index) => (
    <span key={community.subdomain}>
      {index > 0 ? (index === communities.length - 1 ? ' and ' : ', ') : null}
      <CommunitySiteLink community={community} />
    </span>
  ))
}

export function EdunodesExistingAccountNotice({
  communities,
  className,
  variant = 'forgot-password',
  email,
}: EdunodesExistingAccountNoticeProps) {
  const communityCount = communities.length
  const communityWord = communityCount === 1 ? 'community' : 'communities'
  const forgotPasswordHref = email
    ? `/auth/forgot-password?email=${encodeURIComponent(email)}`
    : '/auth/forgot-password'

  return (
    <div
      className={cn(
        'mb-6 rounded-xl border border-blue-200 bg-blue-50/80 px-4 py-4 text-sm text-slate-700',
        className
      )}
      role="status"
    >
      <p className="leading-relaxed">
        You have already registered an account on {formatCommunitySitesPhrase(communities)}, which
        {communityCount === 1 ? ' is' : ' are'} one of{' '}
        <a
          href={EDUNODES_HOME_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-blue-700 underline-offset-2 hover:underline"
        >
          Edunodes
        </a>{' '}
        {communityWord}.
        {variant === 'forgot-password'
          ? ' Use the form below to reset your password and sign in.'
          : ' Sign in or reset your password to continue.'}
      </p>
      {variant === 'sign-up' ? (
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Link
            href="/sign-in"
            className="font-semibold text-blue-700 underline-offset-2 hover:underline"
          >
            Sign in
          </Link>
          <Link
            href={forgotPasswordHref}
            className="font-semibold text-blue-700 underline-offset-2 hover:underline"
          >
            Reset password
          </Link>
        </div>
      ) : null}
    </div>
  )
}

export function EdunodesBackToSignInLink({ className }: { className?: string }) {
  return (
    <div className={cn('mt-6 text-center', className)}>
      <Link
        href="/sign-in"
        className="inline-flex items-center justify-center text-sm font-semibold text-slate-500 transition-colors hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 rounded-md"
      >
        Back to sign in
      </Link>
    </div>
  )
}
